/* Organizer profile page (Phase 6) — /{org-slug}/about.
   Part of the organizer website: banner, story, location, contact, socials,
   external website and the real-data stats the organizer chose to show. */
import { Facebook, Globe2, Instagram, Linkedin, Mail, MapPin, Music2, Phone, SearchX, Twitter, Youtube } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { WebsiteFooter } from "@/components/website/WebsiteFooter";
import { WebsiteHeader } from "@/components/website/WebsiteHeader";
import { WebsiteStats } from "@/components/website/WebsiteStats";
import { api, mediaUrlFromMap } from "@/lib/api";
import { ensurePairingLoaded } from "@/lib/fonts";
import { useOrgSlug } from "@/lib/hostTenant";
import { localized } from "@/lib/intl";
import { usePageMeta } from "@/lib/seo";
import { brandStyle, radiusAttr } from "@/lib/theme";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, x: Twitter, linkedin: Linkedin, tiktok: Music2 };

export default function OrganizerProfilePage() {
  const orgSlug = useOrgSlug();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const [data, setData] = useState();

  useEffect(() => {
    setData(undefined);
    api
      .get(`/public/organizers/${orgSlug}`)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null));
  }, [orgSlug]);

  useEffect(() => {
    if (data?.organizer?.theme?.font_pairing) ensurePairingLoaded(data.organizer.theme.font_pairing);
  }, [data?.organizer?.theme?.font_pairing]);

  usePageMeta({
    title: data?.organizer ? `${t("web.about")} — ${data.organizer.name}` : undefined,
    description: data?.organizer ? localized(data.organizer.description, locale) : undefined,
    // WS6: organizador inexistente = 404 de facto → nunca indexable.
    robots: data === null ? "noindex, nofollow" : undefined,
  });

  if (data === undefined) {
    return (
      <div className="flex flex-col gap-6" data-testid="profile-skeleton">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-72 w-full" />
        <div className="max-w-4xl mx-auto w-full px-6">
          <Skeleton className="h-40 rounded-2xl" />
        </div>
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <EmptyState icon={SearchX} testid="profile-not-found" title={t("brand.notFoundTitle")} description={t("brand.notFoundDesc")} />
      </div>
    );
  }

  const { organizer, website } = data;
  const about = (website?.modules || []).find((m) => m.type === "about");
  const text = localized(about?.props?.text, locale) || localized(organizer.description, locale);
  const bannerUrl = mediaUrlFromMap(data.media, website?.banner_media_id) ||
    mediaUrlFromMap(data.media, website?.hero?.media_id);
  const logoUrl = mediaUrlFromMap(data.media, organizer.theme?.logo_media_id);
  const contact = website?.contact || {};
  const email = contact.email || organizer.contact_email;
  const socials = Object.entries(contact.socials || {}).filter(([, url]) => url);

  return (
    <div className="brand-scope bg-white" data-radius={radiusAttr(organizer.theme)} style={brandStyle(organizer.theme)} data-testid="organizer-profile-page">
      <WebsiteHeader organizer={organizer} website={website} media={data.media} />

      {/* banner + identity */}
      <section className="relative bg-[#0A0A0A]" data-testid="profile-banner">
        {bannerUrl && <img src={bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="eager" />}
        <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-16 flex flex-col gap-5">
          {logoUrl && (
            <span className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center overflow-hidden border border-white/20">
              <img src={logoUrl} alt="" className="w-14 h-14 object-contain" />
            </span>
          )}
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white brand-heading" data-testid="profile-name">
            {organizer.name}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-white/65">
            {organizer.location && (
              <span className="inline-flex items-center gap-1.5" data-testid="profile-location">
                <MapPin className="w-4 h-4" aria-hidden="true" />
                {organizer.location}
              </span>
            )}
            {organizer.website && (
              <a href={organizer.website} target="_blank" rel="noopener noreferrer" data-testid="profile-external-site" className="inline-flex items-center gap-1.5 text-white hover:text-white/80 transition-colors duration-200">
                <Globe2 className="w-4 h-4" aria-hidden="true" />
                {t("web.externalSite")}
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 flex flex-col gap-14">
        {text && (
          <div className="flex flex-col gap-4" data-testid="profile-description">
            <h2 className="text-2xl font-light tracking-tight text-[#0A0A0A] brand-heading">{t("web.ourStory")}</h2>
            <p className="text-base text-[#0A0A0A]/70 leading-relaxed whitespace-pre-line">{text}</p>
          </div>
        )}

        <WebsiteStats stats={data.stats} config={website?.stats} />

        <div className="rounded-3xl border border-black/5 bg-[#FAFAF8] p-8 flex flex-col gap-5" data-testid="profile-contact-card">
          <h2 className="text-xl font-medium tracking-tight text-[#0A0A0A]">{t("web.contactUs")}</h2>
          <div className="flex flex-col gap-2.5">
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-2.5 text-sm text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors duration-200">
                <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                {contact.phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="inline-flex items-center gap-2.5 text-sm text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors duration-200 min-w-0 break-all">
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                {email}
              </a>
            )}
            {contact.address && (
              <span className="inline-flex items-start gap-2.5 text-sm text-[#0A0A0A]/70">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                {contact.address}
              </span>
            )}
          </div>
          {socials.length > 0 && (
            <div className="flex items-center gap-2 pt-1">
              {socials.map(([key, url]) => {
                const Icon = SOCIAL_ICONS[key];
                return Icon ? (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key} data-testid={`profile-social-${key}`} className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center text-[#0A0A0A]/50 hover:text-[#0A0A0A] hover:border-black/30 transition-colors duration-200">
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </a>
                ) : null;
              })}
            </div>
          )}
        </div>
      </section>

      <WebsiteFooter organizer={organizer} website={website} media={data.media} />
    </div>
  );
}
